import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import BlogLayout from "../../../components/BlogLayout";
import Venkat from "../../../assets/venkat.jpg";
import AIJudiciary from "../../../assets/AIJudiciary.webp";
import AuthorCard from "../../../components/AuthorCard";
import { FaTwitter, FaLinkedin } from "react-icons/fa";
import "./AIInJudiciary.css";

const AIInJudiciary = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulating data fetch
    const fetchBlog = async () => {
      try {
        // In a real app, this would be an API call
        const mockBlog = {
          title: "Use of AI in Indian Judiciary",
          publishDate: "March 10, 2025",
          author: {
            name: "Venkatesan R",
            image: Venkat,
            bio: "<p>With 11 years of experience in AI, spanning presales, solutions, and delivery, I’ve worked at the intersection of technology and business—leading AI solutions teams, developing accelerators, and crafting strategies that bridge technical capabilities with real-world needs. Managing AI projects has given me valuable insights into anticipating challenges and refining solutions to better meet customer needs.</p><p>Thought Box is my space to explore ideas beyond technology—where different perspectives meet, and curiosity leads the way. Currently residing in Copenhagen, I enjoy theatre, playing badminton, and contributing to performing arts by designing lights for shows.</p>",
            socialLinks: [
              {
                icon: <FaLinkedin size={24} style={{ color: "#0077B5" }} />,
                url: "https://www.linkedin.com/in/venkatesan-r-96061b69/",
              },
            ],
          },
          content: "",
        };
        setBlog(mockBlog);
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <BlogLayout title="Loading...">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </BlogLayout>
    );
  }

  if (!blog) {
    return (
      <BlogLayout title="Error">
        <div className="error-container">
          <h2 className="error-title">Blog post not found</h2>
          <p className="error-message">
            The requested blog post could not be loaded.
          </p>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout title={blog.title} customClass="blog-header">
      <Helmet>
        <title>Use of AI in Indian Judiciary</title>
        <meta
          name="description"
          content="Explore how AI is transforming the Indian judiciary by enhancing efficiency, reducing case backlogs, and enabling faster justice delivery."
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Use of AI in Indian Judiciary" />
        <meta
          property="og:description"
          content="Explore how AI is transforming the Indian judiciary by enhancing efficiency, reducing case backlogs, and enabling faster justice delivery."
        />
        <meta property="og:image" content="https://thought-box.in/og-images/AIJudiciary.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:url"
          content="https://thought-box.in/blog/ai-in-judiciary"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI in Indian Judiciary: Revolutionizing Legal Processes for Faster Justice"
        />
        <meta
          name="twitter:description"
          content="Explore how AI is transforming the Indian judiciary by enhancing efficiency, reducing case backlogs, and enabling faster justice delivery."
        />
        <meta name="twitter:image" content="https://thought-box.in/og-images/AIJudiciary.webp" />
      </Helmet>

      <article className="article-content">
        <h1 className="blog-article-title">{blog.title}</h1>

        <div className="author-meta">
          <img
            src={blog.author.image}
            alt={blog.author.name}
            className="author-image"
          />
          <div className="author-info">
            <p className="author-name">{blog.author.name}</p>
            <p className="publish-date">{blog.publishDate}</p>
          </div>
        </div>

        <div className="blog-article-body">
          {blog.content}
          <p>
            India’s judicial system carries a massive weight—it serves 1.4
            billion people with a structure that includes 18,000 district and
            subordinate courts, 25 High Courts, and one Supreme Court. The
            Supreme Court, with 34 judges, functions as both the final appellate
            court and a constitutional authority, deciding cases that set
            precedents for all courts across the country. But here’s the
            problem: The system is struggling under an enormous backlog of
            cases. The Supreme Court alone sits for about 200 days a year,
            handling nearly 80,000 pending cases. Zooming out to the entire
            system, the numbers become even more staggering, with 47 million
            pending cases as of February 2025.
          </p>
          <h2>The Challenges: Why Justice in India Is Often Delayed</h2>
          <p>
            One of the biggest hurdles is the judge-to-population ratio. India
            has about 21-22 judges per million people, whereas countries like
            the US and China have over 100 judges per million. This shortage
            slows down the entire judicial process, making it hard to keep up
            with the increasing inflow of cases. Former Chief Justice D.Y.
            Chandrachud, at the India Today Conclave 2023, highlighted these
            challenges, stressing the need for modernization and tech-driven
            solutions. During his tenure, he laid out a clear two-year plan for
            judicial transformation, focusing on digitization, transparency, and
            accessibility. Many of his initiatives have already set the stage
            for a tech-driven judiciary.
          </p>
          <h2>
            Technology in the Indian Judiciary: A Step in the Right Direction
          </h2>
          <p>
            Under Justice D.Y. Chandrachud’s leadership, the Supreme Court has
            seen transformational steps in leveraging technology. Some of his
            key initiatives include:
          </p>
          <ul>
            <li>
              <h3>National Judicial Data Grid (NJDG):</h3> The real-time case
              data of the Supreme Court is now integrated into NJDG, making
              pendency and case status data publicly accessible for the first
              time. This move significantly improves transparency and
              accountability.{" "}
            </li>
            <li>
              <h3>Virtual Courtrooms & Hybrid Hearings:</h3> The adoption of
              video conferencing during the COVID-19 pandemic was a game
              changer. Hybrid hearings now allow lawyers to argue cases both
              physically and virtually, improving efficiency.{" "}
            </li>
            <li>
              <h3>Live Streaming of Cases:</h3> Key Constitution Bench hearings
              are now live-streamed, allowing public access to landmark cases in
              real time.{" "}
            </li>
            <li>
              <h3>Electronic Supreme Court Reports (e-SCR): </h3>Earlier, access
              to Supreme Court judgments required costly subscriptions
              (₹30,000–₹50,000). With e-SCR, all Supreme Court judgments are now
              freely available, ensuring easy access to legal knowledge for
              lawyers, students, and researchers.
            </li>
          </ul>{" "}
          <p>
            These initiatives set a strong foundation, but AI is now taking
            things to the next level.
          </p>
          <h2>
            The Role of AI in the Indian Judiciary: How Smart Tech Is Making a
            Difference
          </h2>
          <p>
            Beyond basic digitization, Artificial Intelligence (AI) is actively
            transforming judicial processes, making them faster and more
            efficient.
          </p>
          <h3>1. AI for Translation & Transcription</h3>
          <p>
            One of the most revolutionary AI-driven changes is automated
            translation of judgments. While English is the primary language of
            the Supreme Court and High Courts, most Indians are more comfortable
            with regional languages. AI-powered translation tools are ensuring
            that court judgments are available in all Indian languages, making
            legal information accessible to everyone.{" "}
          </p>
          <p>
            Additionally, live transcription of court proceedings is now in use,
            creating accurate, real-time records of cases. This eliminates
            delays in documenting judgments and improves access to legal
            records.
          </p>
          <h3>2. AI for Categorization of Cases</h3>
          <p>
            Sorting millions of cases manually is impossible. AI is now
            automating case categorization, ensuring that similar cases are
            grouped together and heard by the same judge or bench.
          </p>
          <p>
            {" "}
            For instance, Justice Chandrachud shared an example at Oxford
            University, where one of his colleagues disposed of 750 cases with a
            single judgment, thanks to AI-assisted categorization.
          </p>
          <p>The benefits of AI-driven case categorization include:</p>
          <ul>
            {" "}
            <li>Faster case disposal by handling similar cases in bulk</li>
            <li>Reducing redundancy in hearings and arguments</li>
            <li> Ensuring consistency in judgments for similar cases</li>
            <li> Prioritizing urgent matters, preventing unnecessary delays</li>
          </ul>
          <p>
            With a backlog of 47 million cases, such AI-driven efficiency is a
            game-changer.
          </p>
          <h3>3. AI-Powered Legal Search Assistants: The Use of GenAI & RAG</h3>
          <p>
            The Supreme Court of India has taken a major leap in AI adoption
            with the development of an AI-powered judicial search assistant,
            designed to help judges quickly retrieve relevant case laws,
            precedents, and legal references.
          </p>
          <h4>How does it work?</h4>
          <p>
            The Supreme Court’s judgments are converted into embeddings using
            NVIDIA NeMo, stored in a GPU-accelerated vector database. When a
            judge queries a case, the NeMo Retriever pulls up relevant
            judgments, which are then refined by the NeMo Reranking
            microservice. The final results are processed by a large language
            model (LLM) to generate a natural-language response, including
            necessary verdicts.
          </p>
          <h4>Impact & Future Plans</h4>
          <ul>
            <li>
              Faster legal research – Judges can quickly access relevant case
              laws.
            </li>
            <li>
              {" "}
              Accurate analysis – AI ensures the proper referencing of
              precedents.
            </li>
            <li>
              {" "}
              Scalable – Currently used in the Supreme Court, with plans for
              wider implementation across the judiciary.
            </li>
          </ul>
          <p>
            Additionally, AI-based summarization tools provide quick, concise
            summaries of legal pleadings, saving time for judges and lawyers.
          </p>
          <h2>Is This Enough? Do We Need More Rigor?</h2>
          <p>
            AI is already streamlining processes in the Indian judiciary, but is
            it enough to tackle the challenges we face, especially with the
            massive backlog of cases?{" "}
          </p>
          <p>
            Given the urgency of reducing delays and improving efficiency,
            should we be moving faster in adopting AI across courts? Could AI
            play a bigger role in speeding up case resolutions and transforming
            the legal landscape?
          </p>
          <p>
            {" "}
            It’s clear that AI has immense potential, but we need to ensure its
            thoughtful integration. What are your thoughts on this? Let’s
            continue the conversation!
          </p>
        </div>

        <AuthorCard author={blog.author} />
      </article>
    </BlogLayout>
  );
};

export default AIInJudiciary;
